import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/api';

// Floating notification for center-students: shows when their teacher has started
// a live Study Session, with "Join Google Meet" + "Current Task" buttons. Polls
// /student/study-sessions/active every 30s (no websockets — matches the app's
// polling realtime pattern). No-ops for students not in an active session.
//
// Hidden while the student is inside an exam room (so it never overlaps a test).
const HIDDEN_ON = ['/reading_test_room', '/listening_test_room', '/writing_test_room', '/speaking_test_room'];

export default function StudySessionBanner() {
  const [session, setSession] = useState(null);
  const [dismissed, setDismissed] = useState(null);   // session_id the user closed
  const navigate = useNavigate();
  const location = useLocation();

  const load = () => {
    const token = localStorage.getItem('token');
    if (!token) { setSession(null); return; }
    fetch(`${API_BASE}/student/study-sessions/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSession(d ? d.session : null))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  if (!session) return null;
  if (dismissed === session.session_id) return null;
  if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) return null;

  const task = session.current_task;
  const openTask = () => { if (task?.url) navigate(task.url); };

  return (
    <div className="fixed bottom-4 right-4 z-[70] w-[340px] max-w-[90vw] rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden animate-[fadeIn_0.3s_ease]">
      <div className="bg-gradient-to-r from-[#0096b1] to-[#2b5356] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white font-bold">
          <span className="text-lg">📚</span> Buổi học đã bắt đầu
        </div>
        <button onClick={() => setDismissed(session.session_id)} className="text-white/80 hover:text-white text-xl leading-none" title="Ẩn">×</button>
      </div>
      <div className="p-4 space-y-3">
        {session.teacher_name && (
          <div className="text-sm text-gray-500">Giáo viên: <span className="font-semibold text-[#2b5356]">{session.teacher_name}</span></div>
        )}
        {task && (
          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Bài hiện tại</div>
            <div className="text-sm font-medium text-gray-800 truncate">{task.title}</div>
            <div className="text-xs text-gray-400 capitalize">
              {task.skill}{task.parts_label ? ` · ${task.parts_label}` : ''}
              {task.locked && <span className="ml-1 text-[#eb7e37] font-semibold normal-case">· Cần VIP</span>}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          {session.meet_url && (
            <a href={session.meet_url} target="_blank" rel="noreferrer"
              className="flex-1 text-center text-sm font-semibold text-[#0096b1] bg-[#0096b1]/10 hover:bg-[#0096b1]/20 rounded-lg py-2.5 transition-colors">
              Join Google Meet
            </a>
          )}
          {/* Khoá VIP giống hệt chuông "Bài tập được giao": học viên trung tâm chưa có VIP
              thì thấy nút nâng cấp chứ không vào thẳng bài thi (feedback 03/10). Cờ `locked`
              do máy chủ quyết, giao diện không tự đoán. */}
          {task?.url && (task.locked ? (
            <button onClick={() => navigate('/vip-packages')}
              title="Cần gói VIP để làm bài này"
              className="flex-1 text-center text-sm font-semibold text-white bg-[#eb7e37] hover:bg-[#d96f2b] rounded-lg py-2.5 transition-colors">
              Nâng cấp VIP
            </button>
          ) : (
            <button onClick={openTask}
              className="flex-1 text-center text-sm font-semibold text-white bg-[#0096b1] hover:bg-[#007d93] rounded-lg py-2.5 transition-colors">
              Bài hiện tại
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
